import React, {useState, useRef, useEffect} from "react";
import {useOutletContext} from "react-router-dom";
import {useSelector} from "react-redux";
import {
    Message,
    Modal,
    Breadcrumb,
    Card,
    Grid,
    Form,
    Space,
    Button,
    Divider,
    Table,
    Pagination,
    Input,
    Dropdown,
    Menu,
    Select,
    Image,
    Tag,
} from "@arco-design/web-react";
import {IconSearch, IconUndo, IconPlusCircle, IconEye, IconDelete, IconUpload, IconDownload} from "@arco-design/web-react/icon";
import {Scrollbars} from "react-custom-scrollbars-2";
import PropTypes from "prop-types";

import Util from "../../../../../nowui-common/js/util";

import Error from "../../../../../nowui-common/component/error";
import Import from "../../../../../nowui-common/component/import";
import Process from "../../../../../nowui-common/component/process";
import Loading from "../../../../../nowui-common/component/loading";
import Selected from "../../../../../nowui-common/component/selected";
import Sort from "../../../../../nowui-common/component/sort";

import RiskDetail from "./detail";

const RiskIndex = ({autoHeight = false, selectType = "checkbox", onSelect = () => {}, ...props}) => {
    const outletContext = useOutletContext();
    const main = useSelector((store) => store.main);
    const controller = new AbortController();
    const request = useRef(false);
    const [form] = Form.useForm();
    const columnListRef = useRef([]);
    const [columnList, setColumnList] = useState([]);
    const [operationColumn, setOperationColumn] = useState({});
    const [isLoad, setIsLoad] = useState(false);
    const [isDetail, setIsDetail] = useState(false);
    const [isProcess, setIsProcess] = useState(false);
    const [processType, setProcessType] = useState(0);
    const [isImport, setIsImport] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [selectedIdList, setSelectedIdList] = useState([]);
    const [selectedList, setSelectedList] = useState([]);
    const [errorTitle, setErrorTitle] = useState("");
    const [errorList, setErrorList] = useState([]);
    const [breadcrumbList, setBreadcrumbList] = useState([]);
    const [action, setAction] = useState("");
    const [riskId, setRiskId] = useState("");
    const [riskCount, setRiskCount] = useState(0);
    const [riskList, setRiskList] = useState([]);
    const [riskPageIndex, setRiskPageIndex] = useState(1);
    const [riskPageSize, setRiskPageSize] = useState(10);
    const [sortList, setSortList] = useState([]);

    useEffect(() => {
        handleSearchRisk();

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        setErrorTitle("");
        setErrorList([]);

        let breadcrumbList = Util.getBreadcrumb(outletContext, main.menuList, "/risk/index");
        setBreadcrumbList(breadcrumbList);

        let columnList = [
            {
                dataIndex: "riskName",
                title: outletContext.intl.formatMessage({id: "risk.riskName"}),
            },
            {
                dataIndex: "riskImage",
                title: outletContext.intl.formatMessage({id: "risk.riskImage"}),
                render: (col, record, index) => (
                    <React.Fragment>
                        {record.riskImage ? <Image width={50} src={process.env.FILE_URL + record.riskImage} alt="" /> : null}
                    </React.Fragment>
                ),
            },
            {
                dataIndex: "riskStatus",
                title: outletContext.intl.formatMessage({id: "risk.riskStatus"}),
                render: (col, record, index) => (
                    <React.Fragment>
                        {record.riskStatus ? (
                            <Tag color="#165dff" size="medium">
                                {outletContext.intl.formatMessage({id: "global.on"})}
                            </Tag>
                        ) : (
                            <Tag color="#f53f3f" size="medium">
                                {outletContext.intl.formatMessage({id: "global.off"})}
                            </Tag>
                        )}
                    </React.Fragment>
                ),
            },
            {
                dataIndex: "createTime",
                title: outletContext.intl.formatMessage({id: "global.createTime"}),
            },
        ];
        setColumnList(columnList);

        setOperationColumn({
            width: 110,
            fixed: "right",
            dataIndex: "operation",
            title: outletContext.intl.formatMessage({id: "global.operation"}),
            render: (col, record, index) => (
                <React.Fragment>
                    <Button
                        type="default"
                        icon={<IconEye />}
                        onClick={() => {
                            setIsDetail(true);
                            setAction("update");
                            setRiskId(record.riskId);
                        }}
                    >
                        {outletContext.intl.formatMessage({id: "global.view"})}
                    </Button>
                </React.Fragment>
            ),
        });

        Util.changeSortList(sortList, setSortList, columnList);
    }, [localStorage.getItem("locale")]);

    useEffect(() => {
        onSelect(selectedList);
    }, [selectedList]);

    useEffect(() => {
        columnListRef.current = columnList;
    }, [columnList]);

    useEffect(() => {
        let columnList = columnListRef.current;
        if (columnList.length == 0) {
            return;
        }
        for (let i = 0; i < columnList.length; i++) {
            let direction = "";
            for (let j = 0; j < sortList.length; j++) {
                if (columnList[i].dataIndex == sortList[j].field) {
                    direction = sortList[j].direction;
                    break;
                }
            }
            columnList[i].sortOrder = direction;
        }
        setColumnList([...columnList]);

        handleSearchRisk();
    }, [sortList]);

    const handleSearchRisk = () => {
        handleLoadRiskList(1, riskPageSize);
    };

    const handleLoadRiskList = (riskPageIndex, riskPageSize) => {
        if (request.current) {
            return;
        }
        request.current = true;

        setIsLoad(true);
        setErrorTitle("");
        setErrorList([]);

        let data = form.getFieldsValue();
        data.sortList = sortList;
        data.pageIndex = riskPageIndex;
        data.pageSize = riskPageSize;

        Util.request(outletContext, {
            url: process.env.API_URL + "/risk/frontend/website/v1/list",
            controller: controller,
            data: data,
            success: (response) => {
                setRiskCount(response.count);
                setRiskList(response.data);
                setRiskPageIndex(riskPageIndex);
                setRiskPageSize(riskPageSize);

                request.current = false;
                setIsLoad(false);
            },
            error: (response) => {
                Message.error(response.message);

                setErrorTitle(response.message);
                setErrorList(response.error);

                request.current = false;
                setIsLoad(false);
            },
            complete: () => {},
        });
    };

    const handleDeleteRisk = () => {
        if (selectedIdList.length == 0) {
            return;
        }

        if (request.current) {
            return;
        }

        Modal.confirm({
            title: outletContext.intl.formatMessage({id: "global.deleteConfirmTitle"}),
            content: outletContext.intl.formatMessage({id: "global.deleteConfirmContent"}),
            okButtonProps: {
                status: "danger",
            },
            onOk: () => {
                request.current = true;

                Message.loading({
                    id: "loading",
                    content: outletContext.intl.formatMessage({id: "global.loading"}),
                    duration: 0,
                });

                setIsLoad(true);
                setErrorTitle("");
                setErrorList([]);

                Util.request(outletContext, {
                    url: process.env.API_URL + "/risk/frontend/website/v1/delete",
                    controller: controller,
                    data: {
                        riskIdList: selectedIdList,
                    },
                    success: (response) => {
                        Message.success({
                            id: "loading",
                            content: response.message,
                        });

                        request.current = false;
                        setIsLoad(false);

                        setSelectedIdList([]);
                        setSelectedList([]);

                        handleLoadRiskList(riskPageIndex, riskPageSize);
                    },
                    error: (response) => {
                        Message.error({
                            id: "loading",
                            content: response.message,
                        });

                        setErrorTitle(response.message);
                        setErrorList(response.error);

                        request.current = false;
                        setIsLoad(false);
                    },
                    complete: () => {},
                });
            },
        });
    };

    return (
        <React.Fragment>
            <Scrollbars
                autoHeight={autoHeight}
                autoHeightMax={main.height - 300}
                renderView={({style, ...props}) => {
                    return <div className="scrollbar" style={style} {...props} />;
                }}
            >
                <Breadcrumb className="breadcrumb">
                    {breadcrumbList.map((breadcrumb, breadcrumbIndex) => {
                        return <Breadcrumb.Item key={breadcrumbIndex}>{breadcrumb.name}</Breadcrumb.Item>;
                    })}
                    {props.breadcrumb ? <Breadcrumb.Item key={props.breadcrumb.name}>{props.breadcrumb.name}</Breadcrumb.Item> : null}
                </Breadcrumb>
                <Error errorTitle={errorTitle} errorList={errorList} setErrorTitle={setErrorTitle} setErrorList={setErrorList} />
                <Form form={form} autoComplete="off" layout="vertical">
                    <Card className="card" title={outletContext.intl.formatMessage({id: "global.searchTable"})} bordered={false}>
                        <Grid.Row gutter={20}>
                            <Grid.Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                <Form.Item label={outletContext.intl.formatMessage({id: "risk.riskName"})} field="riskName">
                                    <Input
                                        placeholder={outletContext.intl.formatMessage({id: "global.pleaseEnter"})}
                                        allowClear
                                        onPressEnter={() => {
                                            handleSearchRisk();
                                        }}
                                    />
                                </Form.Item>
                            </Grid.Col>
                            <Grid.Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                <Form.Item label={outletContext.intl.formatMessage({id: "risk.riskStatus"})} field="riskStatus">
                                    <Select
                                        placeholder={outletContext.intl.formatMessage({id: "global.pleaseSelect"})}
                                        allowClear
                                        onChange={(event) => {
                                            handleSearchRisk();
                                        }}
                                    >
                                        <Select.Option value={1}>{outletContext.intl.formatMessage({id: "global.on"})}</Select.Option>
                                        <Select.Option value={0}>{outletContext.intl.formatMessage({id: "global.off"})}</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Grid.Col>
                        </Grid.Row>
                        <Space className="action" wrap>
                            <React.Fragment>
                                <Button
                                    type="primary"
                                    icon={<IconSearch />}
                                    loading={isLoad}
                                    onClick={() => {
                                        handleSearchRisk();
                                    }}
                                >
                                    {outletContext.intl.formatMessage({id: "global.search"})}
                                </Button>
                                <Button
                                    icon={<IconUndo />}
                                    loading={isLoad}
                                    onClick={() => {
                                        form.resetFields();

                                        setSelectedIdList([]);
                                        setSelectedList([]);
                                        setSortList([]);
                                    }}
                                >
                                    {outletContext.intl.formatMessage({id: "global.reset"})}
                                </Button>
                                <Divider type="vertical" />
                                <Button
                                    icon={<IconPlusCircle />}
                                    onClick={() => {
                                        setIsDetail(true);
                                        setAction("save");
                                        setRiskId("");
                                    }}
                                >
                                    {outletContext.intl.formatMessage({id: "global.save"})}
                                </Button>
                                <Button
                                    icon={<IconDelete />}
                                    disabled={selectedIdList.length == 0}
                                    onClick={() => {
                                        handleDeleteRisk();
                                    }}
                                >
                                    {outletContext.intl.formatMessage({id: "global.delete"})}
                                </Button>
                            </React.Fragment>
                        </Space>
                        <Sort sortList={sortList} setSortList={setSortList} />
                        <Table
                            className="table"
                            columns={columnList.concat([operationColumn])}
                            rowKey="riskId"
                            data={riskList}
                            rowSelection={{
                                type: selectType,
                                columnWidth: 50,
                                selectedRowKeys: selectedIdList,
                                onChange: (selectedRowKeys) => {
                                    setSelectedIdList(Util.getSelectedIdList(selectedIdList, riskList, "riskId", selectedRowKeys));
                                    setSelectedList(Util.getSelectedList(selectedList, riskList, "riskId", selectedRowKeys));
                                },
                            }}
                            scroll={{x: 50 + columnList.length * 150 + operationColumn.width}}
                            border={false}
                            pagination={false}
                            onChange={(pagination, sorter, filters) => {
                                setSortList(Util.getSortList(sortList, sorter, columnList));
                            }}
                        />
                        <div className="pagination">
                            <div className="pagination_selected">
                                <Button
                                    onClick={() => {
                                        setIsSelected(true);
                                    }}
                                >
                                    {outletContext.intl.formatMessage({id: "global.selected"})} {selectedIdList.length}
                                </Button>
                            </div>
                            {typeof riskCount == "undefined" ? null : (
                                <Pagination
                                    current={riskPageIndex}
                                    total={riskCount}
                                    simple={main.isMobile}
                                    showTotal
                                    sizeCanChange={!main.isMobile}
                                    onChange={(pageIndex, pageSize) => {
                                        handleLoadRiskList(pageIndex, pageSize);
                                    }}
                                />
                            )}
                        </div>
                    </Card>
                </Form>
                <div className="card_bottom"></div>
            </Scrollbars>
            {isLoad ? <Loading /> : null}
            {isDetail ? (
                <RiskDetail
                    visible={isDetail}
                    action={action}
                    riskId={riskId}
                    onClose={() => {
                        setIsDetail(false);
                    }}
                    onSubmit={() => {
                        setIsDetail(false);

                        handleLoadRiskList(riskPageIndex, riskPageSize);
                    }}
                />
            ) : null}
        </React.Fragment>
    );
};

RiskIndex.propTypes = {
    autoHeight: PropTypes.bool,
    breadcrumb: PropTypes.object,
    selectType: PropTypes.string,
    onSelect: PropTypes.func,
};

export default Util.withRouter(RiskIndex);
